import { FC, ReactNode } from "react";
import cn from "classnames";
import { useTabContext } from "./TabContext";

export type TabPanelsAnimationType = "none" | "slide" | "fade" | "fadeInUp";

type TabPanelsProps = {
  animation?: TabPanelsAnimationType;
};

type TabPanelsWithAnimationProps = {
  tabs: { tabContent: ReactNode }[];
  activeTab: number;
};

const TabPanelsWithoutAnimation: FC<TabPanelsWithAnimationProps> = ({
  tabs,
  activeTab,
}) => (
  <div className="relative">
    {tabs.map((tab, idx) => (
      <div key={idx} className={idx === activeTab ? "block" : "hidden"}>
        {tab.tabContent}
      </div>
    ))}
  </div>
);

const TabPanelsWithSlideAnimation: FC<TabPanelsWithAnimationProps> = ({
  tabs,
  activeTab,
}) => (
  <div className="relative w-full overflow-hidden">
    <div
      className="flex w-full transition-transform duration-300 ease-in-out"
      style={{ transform: `translateX(-${activeTab * 100}%)` }}
    >
      {tabs.map((tab, index) => (
        <div key={index} className="w-full min-w-full shrink-0">
          {tab.tabContent}
        </div>
      ))}
    </div>
  </div>
);

const TabPanelsWithFadeAnimation: FC<TabPanelsWithAnimationProps> = ({
  tabs,
  activeTab,
}) => (
  <div className="relative">
    {tabs.map((tab, index) => (
      <div
        key={index}
        className={cn(
          "absolute top-0 left-0 w-full transition-opacity duration-300 ease-in-out",
          index === activeTab ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        {tab.tabContent}
      </div>
    ))}
  </div>
);

const TabPanelsWithFadeInUpAnimation: FC<TabPanelsWithAnimationProps> = ({
  tabs,
  activeTab,
}) => (
  <div className="relative md:grid">
    {tabs.map((tab, index) => (
      <div
        key={index}
        className={cn(
          "w-full md:col-start-1 md:row-start-1",
          index === activeTab
            ? "animate-fadeInUp relative z-10"
            : "pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 md:relative md:inset-auto",
        )}
      >
        {tab.tabContent}
      </div>
    ))}
  </div>
);

const TAB_PANELS_ANIMATION_COMPONENTS: Record<
  TabPanelsAnimationType,
  FC<TabPanelsWithAnimationProps>
> = {
  none: TabPanelsWithoutAnimation,
  slide: TabPanelsWithSlideAnimation,
  fade: TabPanelsWithFadeAnimation,
  fadeInUp: TabPanelsWithFadeInUpAnimation,
};

export const TabPanels: FC<TabPanelsProps> = ({ animation = "none" }) => {
  const context = useTabContext();

  if (!context) {
    return null;
  }

  const { activeTab = 0, tabs = [] } = context;

  if (!tabs.length) {
    return null;
  }

  const TabPanelsWithAnimation = TAB_PANELS_ANIMATION_COMPONENTS[animation];

  return <TabPanelsWithAnimation tabs={tabs} activeTab={activeTab} />;
};
